import template from './report_employee_hours.html';
import * as Utils from '../../lib/savanna_utils.js';
import tr from '../../lib/default-translator.js';

class ReportEmployeeHoursViewModel
{
	constructor(page)
	{
		this.page = page;
		let first_day = moment().format('YYYY-MM-01');
		let last_day = moment().format('YYYY-MM-DD');

		this.date_start = ko.observable(new Date(first_day).toJSON().slice(0, 10));
		this.date_end = ko.observable(new Date(last_day).toJSON().slice(0, 10));

		this.attendance_data = ko.observableArray([]);
		this.summary_total_hours = ko.observable(0);
		this.summary_employees_count = ko.observable(0);
		this.table_visible = ko.observable(false);
		this.show_hint = ko.observable(true);
		this.show_no_data_message = ko.observable(false);

		this.current_company_id = ko.observable();
		this.current_department_id = ko.observable();
		this.current_employee_id = ko.observable();
		this.department_id = ko.observable();
		this.selected_company = ko.observable();
		this.selected_employee = ko.observable();
		this.selected_department = ko.observable();

		this.interval_options = ko.observableArray(['weekly', 'monthly']);
		this.selected_interval = ko.observable('weekly');
		this.week_start_options = ko.observableArray(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']);
		this.selected_week_start = ko.observable('Monday');

		this.employees_data = ko.observableArray([]);
		this.is_edit_mode = ko.observable(false);
		this.attendance_data_to_save = ko.observableArray([]);
		this.edited_task_start_times = ko.observableArray([]);
		this.edited_task_end_times = ko.observableArray([]);
		this.edited_comments = ko.observableArray([]);
		this.employee_totals = ko.observable({});
		this.overtime_types_columns = ko.observableArray([]);
		this.leave_hours = ko.observable();
		this.holiday_hours = ko.observable();

		this.overtime_type = ko.observable();

		this.include_resigned = ko.observable(false);
		this.overall_overtime_totals = ko.observable({});
		this.overall_totals = ko.observable({});

		this.get_applicable_overtime_type = this.get_applicable_overtime_type.bind(this);
		this.editing_rows = ko.observableArray([]);

		// Cache for overtime type lookups: key = "work_day|is_holiday" -> result
		this._overtime_cache = {};

		this.selected_interval.subscribe((newValue) => {
			if (newValue != null)
				this.selected_interval(newValue);
			else
				this.selected_interval('weekly');
			this.page.clear_entries();
		});
		
		this.selected_company.subscribe((newValue) => {
			this.attendance_data([]);
			this.employees_data([]);
			this.show_hint(true);
			if (newValue != null)
			{
				this.current_company_id(newValue.company_id);
				if (!this.selected_company())
					this.current_company_id(null);
			}
			else
			{
				this.current_company_id(null);
				this.current_department_id(null);
				this.current_employee_id(null);
			}
			this.page.clear_entries();
		});

		this.selected_employee.subscribe((newValue) => {
			this.attendance_data([]);
			this.employees_data([]);
			this.show_hint(true);
			if (newValue != null)
			{
				this.current_employee_id(newValue.employee_id);
				if (!this.selected_department())
					this.current_department_id(null);
			}
			else
			{
				this.current_employee_id(null);
				this.current_department_id(null);
			}
			this.page.clear_entries();
		});

		this.selected_department.subscribe((newValue) => {
			this.attendance_data([]);
			this.show_hint(true);
			this.employees_data([]);

			if (newValue != null)
				this.current_department_id(newValue.department_id);
			else
			{
				this.current_department_id(null);
				this.overtime_types_columns([]);
			}
			this.page.clear_entries();
		});

		this.end_date_timeout = null;
		this.start_date_timeout = null;

		this.date_start.subscribe((newValue) => {
			if (!newValue) return;

			if (this.start_date_timeout)
				clearTimeout(this.start_date_timeout);

			this.start_date_timeout = setTimeout(() => {
				let sd = moment(newValue, "YYYY-MM-DD", true);
				if (!sd.isValid()) return;

				let month_end = moment(sd).endOf('month').format('YYYY-MM-DD');
				this.date_end(month_end);
				this.page.updateData();
			}, 500);
			this.page.clear_entries();
		});

		this.date_end.subscribe((newValue) => {
			if (!newValue) return;

			if (this.end_date_timeout)
				clearTimeout(this.end_date_timeout);

			this.end_date_timeout = setTimeout(() => {
				let ed = moment(newValue, "YYYY-MM-DD", true);
				if (!ed.isValid()) return;

				if (newValue.length === 10)
				{
					let sd = moment(this.date_start(), "YYYY-MM-DD", true);
					if (sd.isValid() && ed.isBefore(sd))
					{
						Grape.alerts.alert({ type: 'error', title: tr('Invalid Date Range'), message: tr('End date cannot be before start date') });
						return;
					}
				}
				this.page.updateData();
			}, 1000);
			this.page.clear_entries();
		});
	}

	is_row_editing(row)
	{
		return this.editing_rows().some(editingRow =>
			editingRow.employee_id === row.employee_id &&
			editingRow.work_day === row.work_day
		);
	}

	toggle_row_edit_mode(row)
	{
		const isCurrentlyEditing = this.is_row_editing(row);

		if (isCurrentlyEditing)
		{
			this.editing_rows.remove(editingRow =>
				editingRow.employee_id === row.employee_id &&
				editingRow.work_day === row.work_day
			);
		}
		else
		{
			this.editing_rows.push({
				employee_id: row.employee_id,
				work_day: row.work_day,
				original_data: { ...row }
			});
		}
	}

	cancel_row_edit(row)
	{
		const editingRow = this.editing_rows().find(editingRow =>
			editingRow.employee_id === row.employee_id &&
			editingRow.work_day === row.work_day
		);

		if (editingRow)
		{
			Object.assign(row, editingRow.original_data);
			this.editing_rows.remove(editingRow);
			this.attendance_data.valueHasMutated();
		}
	}

	async save_single_row(row)
	{
		if (!row._manual_total_override &&
			row.task_start_time && row.task_end_time &&
			!(row.task_start_time === '00:00' && row.task_end_time === '00:00'))
		{
			this.calculate_hours(row);
		}

		let work_day = row.work_day;
		if (work_day && work_day.includes('/'))
		{
			work_day = row.work_day;
		}
		else
		{
			let date = new Date(row.work_day);
			work_day = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
		}

		let task_start_time = row.task_start_time || row.start_of_working_hours || '00:00';
		let task_end_time = row.task_end_time || row.end_of_working_hours || '00:00';

		if (task_start_time && task_start_time.length > 5)
			task_start_time = task_start_time.substring(0, 5);
		if (task_end_time && task_end_time.length > 5)
			task_end_time = task_end_time.substring(0, 5);

		let total_hours = 0;
		if (typeof row.task_total_hours === 'function')
			total_hours = parseFloat(row.task_total_hours() || 0);
		else if (row.task_total_hours != null)
			total_hours = parseFloat(row.task_total_hours);
		else
			total_hours = this.convert_to_numeric(row.basic_attendance_hours);

		let edited_data = [{
			employee_code: row.employee_code,
			employee_id: row.employee_id,
			work_day: work_day,
			task_start_time: task_start_time,
			task_end_time: task_end_time,
			task_total_hours: total_hours,
			basic_attendance_hours: row.basic_attendance_hours,
			overtime_type: row.overtime_type,
			overtime_hours: typeof row.overtime_hours === 'function' ?
				(row.overtime_hours() || 0) : (row.overtime_hours || 0),
			overtime_transaction_type_id: row.overtime_transaction_type_id,
			comments: row.comments || row.leave_type || row.holiday_description || '',
			leave_hours: typeof row.leave_hours === 'function' ?
				(row.leave_hours() || 0) : (row.leave_hours || 0),
			holiday_hours: typeof row.holiday_hours === 'function' ?
				(row.holiday_hours() || 0) : (row.holiday_hours || 0),
			leave_type: row.leave_type || '',
			holiday_description: row.holiday_description || '',
			...this.get_overtime_type_values(row)
		}];

		let input_params = {
			employee_id: row.employee_id,
			department_id: this.selected_department()?.department_id || null,
			date_start: this.date_start(),
			date_end: this.date_end(),
			attendance_data: edited_data
		};

		try
		{
			let response = await Grape.fetches.postJSON('/time_management/save_attendance_data', input_params);

			if (response && response.status === 'OK')
			{
				Grape.alerts.alert({
					type: 'success',
					title: 'Success',
					message: tr('Attendance hours saved successfully')
				});

				this.editing_rows.remove(editingRow =>
					editingRow.employee_id === row.employee_id &&
					editingRow.work_day === row.work_day
				);

				await this.page.load_entries({ force: true });
			}
			else
			{
				console.error('Save failed:', response);
				Grape.alerts.alert({
					type: 'error',
					title: 'Error',
					message: tr('Error saving attendance data: ' + (response?.message || 'Unknown error'))
				});
			}
		}
		catch (error)
		{
			console.error('Save error:', error);
			Grape.alerts.alert({
				type: 'error',
				title: 'Error',
				message: tr('Error saving attendance data. Please try again.')
			});
		}
	}

	has_overtime_columns(employee_data)
	{
		if (!employee_data || !employee_data.attendance_data)
			return this.overtime_types_columns().length > 0;

		// Fast path: if no overtime type columns configured, skip full scan
		const columns = this.overtime_types_columns();
		if (columns.length === 0) return false;

		// Build a Set of configured overtime type names for O(1) lookup
		const configured_types = new Set(columns.map(t => t.overtime_type));

		const hasOvertimeData = employee_data.attendance_data.some(attendance => {
			if (!attendance.overtime_totals) return false;
			return Object.keys(attendance.overtime_totals).some(type =>
				configured_types.has(type) && parseFloat(attendance.overtime_totals[type]) > 0
			);
		});

		return hasOvertimeData || columns.length > 0;
	}

	back_to_top()
	{
		document.body.scrollTop = 0;
		document.documentElement.scrollTop = 0;
	}

	async download_clicked(v, e)
	{
		let csv_string = "";
		let tables = document.querySelectorAll('#employee_hours_report');

		tables.forEach((table, index) => {
			let employee_name = table.previousElementSibling.textContent.trim();
			let is_resigned = this.employees_data()[index].is_resigned;
			let formatted_employee_name = is_resigned ? `${employee_name} (RESIGNED)` : employee_name;
			csv_string += `Employee Name: ${formatted_employee_name}\n\n`;

			let headers = [];
			let header_row = table.querySelector('thead tr');
			header_row.querySelectorAll('th span').forEach(th => {
				headers.push(th.textContent.trim());
			});
			csv_string += headers.join(",") + "\n";

			let body_rows = table.querySelectorAll('tbody tr.view');
			body_rows.forEach(row => {
				let cells = Array.from(row.querySelectorAll('td')).map((cell, cellIndex) => {
					let text = cell.textContent.trim();
					if (cellIndex === 1)
						text = text.split('(')[0].trim();
					return text.includes(',') ? `"${text}"` : text;
				});
				csv_string += cells.join(",") + "\n";
			});

			let footer_row = table.querySelector('tfoot tr');
			if (footer_row)
			{
				let footer_cells = Array.from(footer_row.querySelectorAll('td')).map(cell => {
					let text = cell.textContent.trim();
					return text.includes(',') ? `"${text}"` : text;
				});

				let footer_line = [];
				footer_line.push("Totals");
				for (let i = 0; i < 3; i++)
					footer_line.push('');
				footer_line.push(...footer_cells.slice(1));
				csv_string += footer_line.join(",") + "\n";
			}
			csv_string += "\n";
		});

		if (!csv_string) return;

		let blob = new Blob([csv_string], { type: 'text/csv;charset=utf-8;' });
		let link = document.createElement('a');
		link.href = URL.createObjectURL(blob);
		link.setAttribute('download', 'check_attendance_report.csv');
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

	show_report_help()
	{
		Grape.dialog.open('CheckAttendanceReportHelp', { employee_id: this.page.bindings.id });
	}

	// FIX 1: Corrected variable names (actualDay->actual_day, applicableType->applicable_type,
	//         applicable_workdays->applicable_work_days)
	get_applicable_overtime_type(work_day, is_holiday, dayAbbr)
	{
		// Performance: cache results per work_day + is_holiday combination
		const cache_key = `${work_day}|${is_holiday}`;
		if (this._overtime_cache[cache_key] !== undefined)
			return this._overtime_cache[cache_key];

		let department_id = this.selected_department()?.department_id || this.department_id();
		let applicable_type = null;

		let work_day_moment = moment(work_day, 'DD/MM/YYYY');
		let actual_day = work_day_moment.format('dddd');

		this.overtime_types_columns().forEach(type => {
			if (type.department_id === department_id)
			{
				if (type.overtime_transaction_type_id)
				{
					// FIX: was actualDay / applicable_workdays (wrong names)
					if (!is_holiday && type.applicable_work_days.includes(actual_day))
						applicable_type = type;
				}
				else
				{
					if (is_holiday && type.holiday === true)
					{
						applicable_type = type;
					}
					else if (!is_holiday && type.applicable_work_days && type.applicable_work_days.includes(actual_day) && type.holiday !== true)
					{
						applicable_type = type;
					}
				}
			}
		});

		const result = applicable_type ? {
			overtime_type: applicable_type.overtime_type,
			overtime_transaction_type_id: applicable_type.overtime_transaction_type_id
		} : null;

		this._overtime_cache[cache_key] = result;
		return result;
	}

	// Clears the overtime lookup cache (call when department or overtime columns change)
	_clear_overtime_cache()
	{
		this._overtime_cache = {};
	}

	update_total_hours(row, event)
	{
		let v = parseFloat(event?.target?.value);
		if (Number.isNaN(v)) v = 0;
		if (typeof row.task_total_hours === 'function') row.task_total_hours(v);
		else row.task_total_hours = v;
		row._manual_total_override = true;
	}

	update_start_time(data, event)
	{
		let new_time = event.target.value;

		if (new_time && new_time.length === 5)
		{
			data.task_start_time = new_time;

			if (data.task_start_time_observable && typeof data.task_start_time_observable === 'function')
				data.task_start_time_observable(new_time);

			delete data._manual_total_override;
			this.calculate_hours(data);
			this.attendance_data.valueHasMutated();
		}
	}

	update_comment(data, event)
	{
		const v = (event.target.value || '').trim();
		data.comments = v;
	}

	update_end_time(data, event)
	{
		let new_time = event.target.value;

		if (new_time && new_time.length === 5)
		{
			data.task_end_time = new_time;

			if (data.task_end_time_observable && typeof data.task_end_time_observable === 'function')
				data.task_end_time_observable(new_time);

			delete data._manual_total_override;
			this.calculate_hours(data);
			this.attendance_data.valueHasMutated();
		}
	}

	calculate_hours(data)
	{
		let start_time = data.task_start_time || data.start_of_working_hours;
		let end_time = data.task_end_time || data.end_of_working_hours;

		if (!start_time || !end_time) return;

		let start = moment(start_time, 'HH:mm');
		let end = moment(end_time, 'HH:mm');

		if (end.isBefore(start))
			end.add(1, 'day');

		let total_hours = moment.duration(end.diff(start)).asHours();
		data.task_total_hours(total_hours);

		let basic_start = moment(data.start_of_working_hours, 'HH:mm');
		let basic_end = moment(data.end_of_working_hours, 'HH:mm');
		let basic_hours = moment.duration(basic_end.diff(basic_start)).asHours();

		if (!data.leave_hours || typeof data.leave_hours !== 'function')
			data.leave_hours = ko.observable(0);
		else
			data.leave_hours(0);

		if (!data.holiday_hours || typeof data.holiday_hours !== 'function')
			data.holiday_hours = ko.observable(0);
		else
			data.holiday_hours(0);

		if (!data.overtime_hours || typeof data.overtime_hours !== 'function')
			data.overtime_hours = ko.observable(0);
		else
			data.overtime_hours(0);

		this.overtime_types_columns().forEach(type => {
			if (!data[type.overtime_type] || typeof data[type.overtime_type] !== 'function')
				data[type.overtime_type] = ko.observable(0);
			else
				data[type.overtime_type](0);
		});

		data.overtime_type = null;
		data.overtime_transaction_type_id = null;

		if (data.leave_type)
		{
			data.basic_attendance_hours = '0';
			data.leave_hours(basic_hours);
			data.task_total_hours(0);
			data.overtime_hours(0);
		}
		else if (data.holiday_description)
		{
			data.basic_attendance_hours = '0';
			data.holiday_hours(basic_hours);

			if (data.task_start_time && data.task_end_time)
			{
				let overtime_hours = Math.max(0, total_hours);
				let applicable_overtime = this.get_applicable_overtime_type(data.work_day, true, data.day_abbr);

				if (applicable_overtime && overtime_hours > 0)
				{
					if (!data[applicable_overtime.overtime_type] || typeof data[applicable_overtime.overtime_type] !== 'function')
						data[applicable_overtime.overtime_type] = ko.observable(0);
					data[applicable_overtime.overtime_type](overtime_hours);
					data.overtime_type = applicable_overtime.overtime_type;
					data.overtime_transaction_type_id = applicable_overtime.overtime_transaction_type_id;
					data.overtime_hours(overtime_hours);
					data.task_total_hours(total_hours);
				}
			}
			else
			{
				data.overtime_hours(0);
			}
		}
		else
		{
			data.basic_attendance_hours = basic_hours;

			let overtime_hours = Math.max(0, total_hours - basic_hours);

			let work_day_moment = moment(data.work_day, 'DD/MM/YYYY');
			let day_of_week = work_day_moment.day();

			if (day_of_week === 0 || day_of_week === 6)
				overtime_hours = total_hours;

			let applicable_overtime = this.get_applicable_overtime_type(data.work_day, false, data.day_abbr);

			if (applicable_overtime && overtime_hours > 0)
			{
				if (!data[applicable_overtime.overtime_type] || typeof data[applicable_overtime.overtime_type] !== 'function')
					data[applicable_overtime.overtime_type] = ko.observable(0);

				data[applicable_overtime.overtime_type](overtime_hours);
				data.overtime_type = applicable_overtime.overtime_type;
				data.overtime_transaction_type_id = applicable_overtime.overtime_transaction_type_id;
				data.overtime_hours(overtime_hours);
			}
			else
			{
				data.overtime_hours(0);
			}
		}

		this.attendance_data.valueHasMutated();
	}

	// FIX 2: toggle_edit_mode was calling this.is_row_editing(false) which is meaningless.
	//         Now correctly clears the editing_rows array instead.
	toggle_edit_mode()
	{
		this.is_edit_mode(!this.is_edit_mode());
		this.editing_rows([]); // FIX: was is_row_editing(false)
	}

	async save_attendance_data()
	{
		this.attendance_data().forEach(employeeData => {
			employeeData.attendance_data.forEach(attendance => {
				if (
					!attendance._manual_total_override &&
					attendance.task_start_time && attendance.task_end_time &&
					!(attendance.task_start_time === '00:00' && attendance.task_end_time === '00:00')
				)
				{
					this.calculate_hours(attendance);
				}
			});
		});

		let edited_data = this.attendance_data().flatMap(data =>
			data.attendance_data.map(attendance => {
				let work_day = attendance.work_day;
				if (work_day && work_day.includes('/'))
				{
					work_day = attendance.work_day;
				}
				else
				{
					let date = new Date(attendance.work_day);
					work_day = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
				}

				let task_start_time = attendance.task_start_time || attendance.start_of_working_hours || '00:00';
				let task_end_time = attendance.task_end_time || attendance.end_of_working_hours || '00:00';

				if (task_start_time && task_start_time.length > 5)
					task_start_time = task_start_time.substring(0, 5);
				if (task_end_time && task_end_time.length > 5)
					task_end_time = task_end_time.substring(0, 5);

				let total_hours = 0;
				if (typeof attendance.task_total_hours === 'function')
					total_hours = parseFloat(attendance.task_total_hours() || 0);
				else if (attendance.task_total_hours != null)
					total_hours = parseFloat(attendance.task_total_hours);
				else
					// FIX 3: was this.viewModel.convert_to_numeric — viewModel doesn't exist on the ViewModel itself
					total_hours = this.convert_to_numeric(attendance.basic_attendance_hours);

				return {
					employee_code: attendance.employee_code,
					employee_id: attendance.employee_id,
					work_day: work_day,
					task_start_time: task_start_time,
					task_end_time: task_end_time,
					task_total_hours: total_hours,
					basic_attendance_hours: attendance.basic_attendance_hours,
					overtime_type: attendance.overtime_type,
					overtime_hours: typeof attendance.overtime_hours === 'function' ?
						(attendance.overtime_hours() || 0) : (attendance.overtime_hours || 0),
					overtime_transaction_type_id: attendance.overtime_transaction_type_id,
					comments: attendance.comments || attendance.leave_type || attendance.holiday_description || '',
					leave_hours: typeof attendance.leave_hours === 'function' ?
						(attendance.leave_hours() || 0) : (attendance.leave_hours || 0),
					holiday_hours: typeof attendance.holiday_hours === 'function' ?
						(attendance.holiday_hours() || 0) : (attendance.holiday_hours || 0),
					leave_type: attendance.leave_type || '',
					holiday_description: attendance.holiday_description || '',
					...this.get_overtime_type_values(attendance)
				};
			})
		);

		let input_params = {
			employee_id: this.current_employee_id(),
			department_id: this.selected_department()?.department_id || null,
			date_start: this.date_start(),
			date_end: this.date_end(),
			attendance_data: edited_data
		};

		try
		{
			let response = await Grape.fetches.postJSON('/time_management/save_attendance_data', input_params);

			if (response && response.status === 'OK')
			{
				Grape.alerts.alert({
					type: 'success',
					title: 'Success',
					message: tr('Attendance hours saved successfully')
				});
				this.toggle_edit_mode();
				await this.page.load_entries({ force: true });
			}
			else
			{
				console.error('Save failed:', response);
				Grape.alerts.alert({
					type: 'error',
					title: 'Error',
					message: tr('Error saving attendance data: ' + (response?.message || 'Unknown error'))
				});
			}
		}
		catch (error)
		{
			console.error('Save error:', error);
			Grape.alerts.alert({
				type: 'error',
				title: 'Error',
				message: tr('Error saving attendance data. Please try again.')
			});
		}
	}

	get_overtime_type_values(attendance)
	{
		let overtime_values = {};
		this.overtime_types_columns().forEach(type => {
			if (attendance[type.overtime_type])
			{
				overtime_values[type.overtime_type] = typeof attendance[type.overtime_type] === 'function'
					? attendance[type.overtime_type]()
					: attendance[type.overtime_type];
			}
		});
		return overtime_values;
	}

	convert_to_numeric(value)
	{
		if (typeof value === 'number') return value;
		if (!value || value === '0') return 0;

		if (typeof value === 'string')
		{
			if (/^\d+(\.\d+)?$/.test(value))
				return parseFloat(value);
			let [hours, minutes] = value.split(':');
			return parseFloat(hours) + (parseFloat(minutes) / 60);
		}

		return 0;
	}

	update_click()
	{
		this.show_hint(false);

		if (this.current_company_id() && this.current_department_id() && this.current_employee_id() == null)
			Grape.alerts.alert({ type: 'warning', title: tr('Warning'), message: tr('Please select an company, employee or department ') });

		this.page.updateData();
	}
}

class ReportEmployeeHoursPage
{
	constructor(bindings)
	{
		this.bindings = bindings;
		this.viewModel = new ReportEmployeeHoursViewModel(this);
	}

	async init()
	{
		if (await Utils.redirectOnAccessDenied('DepartmentsAvgLeaveComparisonReport'))
			return;

		document.title = tr('Check Attendance Hour Report');
		window.current_page_title(tr('Check Attendance Hour Report'));
		this.updateData();
	}

	async clear_entries()
	{
		this.viewModel.attendance_data([]);
		this.viewModel.employees_data([]);
	}

	async load_entries(opts)
	{
		if (!(opts && opts.force === true)) return;

		this.viewModel.is_edit_mode(false);

		if (this.viewModel.selected_company() || this.viewModel.selected_department() || this.viewModel.selected_employee())
		{
			let params = {
				date_start: this.viewModel.date_start(),
				date_end: this.viewModel.date_end(),
				department_id: this.viewModel.selected_department()?.department_id || null,
				employee_id: this.viewModel.current_employee_id(),
				company_id: this.viewModel.selected_company()?.company_id || null,
				include_resigned: this.viewModel.include_resigned()
			};

			try
			{
				let res = await Grape.fetches.postJSON('/time_management/check_attendance_hours_report', params);

				if (!res || !Array.isArray(res) || res.length === 0)
				{
					Grape.alerts.alert({
						type: 'warning',
						title: tr('No data'),
						message: tr('The selected filters have no employees to show.')
					});
					this.viewModel.attendance_data([]);
					return;
				}

				// Fetch overtime types first so process_data has them available
				if (res[0].department_id)
				{
					await this.fetch_overtime_types(res[0].department_id);
					this.viewModel.department_id(res[0].department_id);
					// Clear overtime cache now that columns may have changed
					this.viewModel._clear_overtime_cache();
				}

				// FIX 4: process_data was iterating employees_data() before it was populated (empty loop).
				//         The stale loop has been removed entirely — it served no purpose.
				let mappedData = this.process_data(res);

				this.viewModel.attendance_data(mappedData);

			}
			catch (error)
			{
				console.error('Error loading attendance data:', error);
				Grape.alerts.alert({
					type: 'error',
					title: tr('Error'),
					message: tr('Failed to load attendance data. Please try again.')
				});
			}
		}
	}

	async updateData()
	{
	}

	async fetch_overtime_types(department_id)
	{
		try
		{
			let options = {
				table: 'overtime_rules',
				schema: 'public',
				filter: [{
					value: department_id,
					operand: '=',
					field: 'department_id'
				}]
			};

			let overtime_types = await Grape.fetches.getJSON('api/record', options);

			if (overtime_types.status !== 'ERROR' && Array.isArray(overtime_types.records) && overtime_types.records.length > 0)
			{
				let overtime_totals = {};

				let processed_types = overtime_types.records.map(type => {
					let applicable_work_days = [];
					if (type.applicable_workdays)
					{
						if (typeof type.applicable_workdays === 'string')
							applicable_work_days = type.applicable_workdays.replace(/[{}]/g, '').split(',').map(day => day.trim());
						else if (Array.isArray(type.applicable_workdays))
							applicable_work_days = type.applicable_workdays;
					}

					overtime_totals[type.overtime_type] = 0;

					return {
						...type,
						overtime_type: type.overtime_type,
						overtime_transaction_type_id: type.overtime_transaction_type_id,
						applicable_work_days: applicable_work_days,
						holiday: type.holiday
					};
				});

				this.viewModel.overtime_types_columns(processed_types);
				this.viewModel.overall_overtime_totals(overtime_totals);
			}
			else
			{
				this.viewModel.overtime_types_columns([]);
				this.viewModel.overall_overtime_totals({});
			}
		}
		catch (error)
		{
			this.viewModel.overtime_types_columns([]);
			this.viewModel.overall_overtime_totals({});
			Grape.alerts.alert({
				type: 'error',
				title: 'Error',
				message: 'Could not load overtime types.'
			});
		}
	}

	// REMOVED: all_employees_with_missing_hours tracking and show_missing_hours_alert call.
	// Employees without time rules are silently skipped — their hours default to 00:00
	// as already handled by the format_time fallback in process_weekly_data / process_monthly_data.
	process_data(results)
	{
		let attendance_data = [];
		let employees_data = [];
		let overall_totals = {
			basic_attendance_hours: 0,
			total_attendance_hours: 0,
			total_leaves_hours: 0,
			total_holiday_hours: 0,
		};

		let overall_overtime_totals = {};
		this.viewModel.overtime_types_columns().forEach(type => {
			overall_overtime_totals[type.overtime_type] = 0;
		});

		// Performance: pre-filter once rather than inside each grouping loop
		let filtered_results = this.viewModel.current_employee_id()
			? results.filter(item => item.employee_id === this.viewModel.current_employee_id())
			: results;

		if (this.viewModel.selected_interval() === 'weekly')
		{
			// Performance: single-pass grouping by employee+week
			let grouped_by_employee_and_week = {};
			for (const item of filtered_results)
			{
				let employee_name = `${item.employee_firstname} ${item.employee_surname}`;
				let week_start = this.get_week_start(new Date(item.work_day), this.viewModel.selected_week_start()).toDateString();

				if (!grouped_by_employee_and_week[employee_name])
					grouped_by_employee_and_week[employee_name] = {};
				if (!grouped_by_employee_and_week[employee_name][week_start])
					grouped_by_employee_and_week[employee_name][week_start] = [];

				grouped_by_employee_and_week[employee_name][week_start].push({ ...item, is_resigned: item.is_resigned });
			}

			for (let employee_name in grouped_by_employee_and_week)
			{
				let employee_week_data = grouped_by_employee_and_week[employee_name];
				for (let week_start in employee_week_data)
				{
					let week_data = employee_week_data[week_start];
					let employee_attendance_data = this.process_weekly_data(week_data, week_start);
					let week_start_date = this.get_week_start(new Date(week_start), this.viewModel.selected_week_start());
					let week_end_date = new Date(week_start_date.getTime() + 6 * 24 * 60 * 60 * 1000);
					let user_start_date = new Date(this.viewModel.date_start());
					let user_end_date = new Date(this.viewModel.date_end());

					let display_start_date = new Date(Math.max(week_start_date.getTime(), user_start_date.getTime()));
					let display_end_date = new Date(Math.min(week_end_date.getTime(), user_end_date.getTime()));

					let date_range = display_start_date.getTime() === display_end_date.getTime()
						? display_start_date.toLocaleDateString()
						: `${display_start_date.toLocaleDateString()} - ${display_end_date.toLocaleDateString()}`;

					attendance_data = attendance_data.concat(employee_attendance_data.attendance_data);

					for (let key in overall_totals)
						overall_totals[key] += employee_attendance_data.totals[key];
					for (let key in employee_attendance_data.overtime_totals)
						overall_overtime_totals[key] += employee_attendance_data.overtime_totals[key];

					employees_data.push({
						employee_name: employee_name,
						week_range: date_range,
						attendance_data: employee_attendance_data.attendance_data,
						totals: employee_attendance_data.totals,
						overtime_totals: employee_attendance_data.overtime_totals,
						is_resigned: employee_attendance_data.attendance_data[0].is_resigned
					});
				}
			}
		}
		else if (this.viewModel.selected_interval() === 'monthly')
		{
			// Performance: single-pass grouping
			let grouped_by_employee = {};
			for (const item of filtered_results)
			{
				let employee_name = `${item.employee_firstname} ${item.employee_surname}`;
				if (!grouped_by_employee[employee_name])
					grouped_by_employee[employee_name] = [];
				grouped_by_employee[employee_name].push(item);
			}

			for (let employee_name in grouped_by_employee)
			{
				let employee_data = grouped_by_employee[employee_name];
				let employee_attendance_data = this.process_monthly_data(employee_data);

				attendance_data = attendance_data.concat(employee_attendance_data.attendance_data);

				for (let key in overall_totals)
					overall_totals[key] += employee_attendance_data.totals[key];
				for (let key in employee_attendance_data.overtime_totals)
					overall_overtime_totals[key] += employee_attendance_data.overtime_totals[key];

				employees_data.push({
					employee_name: employee_name,
					date_range: `${this.viewModel.date_start()} to ${this.viewModel.date_end()}`,
					attendance_data: employee_attendance_data.attendance_data,
					totals: employee_attendance_data.totals,
					overtime_totals: employee_attendance_data.overtime_totals,
					is_resigned: employee_attendance_data.attendance_data[0].is_resigned
				});
			}
		}

		// These are set for both intervals (previously monthly returned early, skipping weekly's set)
		this.viewModel.employees_data(employees_data);
		this.viewModel.overall_totals(overall_totals);
		this.viewModel.overall_overtime_totals(overall_overtime_totals);
		return attendance_data;
	}

	process_weekly_data(week_data, week_start)
	{
		let attendance_data = [];
		let week_start_date = this.get_week_start(new Date(week_start), this.viewModel.selected_week_start());
		let week_end_date = new Date(week_start_date.getTime() + 6 * 24 * 60 * 60 * 1000);
		let user_start_date = new Date(this.viewModel.date_start());
		let user_end_date = new Date(this.viewModel.date_end());

		let display_start_date = new Date(Math.max(week_start_date.getTime(), user_start_date.getTime()));
		let display_end_date = new Date(Math.min(week_end_date.getTime(), user_end_date.getTime()));

		let date_range = display_start_date.getTime() === display_end_date.getTime()
			? display_start_date.toLocaleDateString()
			: `${display_start_date.toLocaleDateString()} - ${display_end_date.toLocaleDateString()}`;

		let totals = {
			basic_attendance_hours: 0,
			total_attendance_hours: 0,
			total_leaves_hours: 0,
			total_holiday_hours: 0,
		};

		let overtime_totals = {};
		this.viewModel.overtime_types_columns().forEach(type => {
			overtime_totals[type.overtime_type] = 0;
		});

		const week_cutoff = new Date(week_start_date.getTime() + 7 * 24 * 60 * 60 * 1000);

		week_data.forEach((item) => {
			let work_day = new Date(item.work_day);
			if (work_day >= week_start_date && work_day < week_cutoff)
			{
				let formatted_work_day = work_day.toLocaleDateString();

				function format_time(val)
				{
					if (val)
					{
						if (/^\d{2}:\d{2}(:\d{2})?$/.test(val))
							return val.slice(0, 5);
						return new Date(val).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
					}
					return '00:00';
				}

				let formatted_start_time = '00:00';
				let formatted_end_time = '00:00';
				if (work_day.getDay() == 0 || work_day.getDay() == 6)
				{
					formatted_start_time = format_time(item.task_start_time);
					formatted_end_time = format_time(item.task_end_time);
				}
				else
				{
					formatted_start_time = format_time(item.task_start_time || item.start_of_working_hours);
					formatted_end_time = format_time(item.task_end_time || item.end_of_working_hours);
				}

				let basic_attendance_hours = (work_day.getDay() === 6 || work_day.getDay() === 0)
					? 0
					: (item.basic_attendance_hours?.hours ?? 0);

				let task_total_hours = parseFloat(item.task_total_hours) || parseFloat(basic_attendance_hours) || 0;

				let leave_hours = item.leave_hours ? parseFloat(item.leave_hours) :
					item.leave_type ? (item.task_start_time && item.task_end_time ?
						parseFloat(basic_attendance_hours) - task_total_hours : parseFloat(basic_attendance_hours)) : 0;

				let holiday_hours;
				if (item.holiday_hours)
					holiday_hours = parseFloat(item.holiday_hours) || 0;
				else if (item.holiday_description)
					holiday_hours = parseFloat(basic_attendance_hours) || 0;
				else
					holiday_hours = 0;

				if (item.leave_type || item.holiday_description)
					basic_attendance_hours = 0;

				totals.basic_attendance_hours += this.viewModel.convert_to_numeric(basic_attendance_hours);
				totals.total_attendance_hours += this.viewModel.convert_to_numeric(task_total_hours) || 0;
				totals.total_leaves_hours += this.viewModel.convert_to_numeric(leave_hours);
				totals.total_holiday_hours += this.viewModel.convert_to_numeric(holiday_hours);

				if (item.overtime_type && overtime_totals.hasOwnProperty(item.overtime_type))
				{
					let current_overtime = this.viewModel.convert_to_numeric(overtime_totals[item.overtime_type] || 0);
					let added_overtime = this.viewModel.convert_to_numeric(item.overtime_hours) || 0;
					overtime_totals[item.overtime_type] = current_overtime + added_overtime;
				}

				attendance_data.push({
					employee_id: item.employee_id,
					work_day: formatted_work_day,
					task_start_time: formatted_start_time,
					task_end_time: formatted_end_time,
					task_total_hours: ko.observable(parseFloat(task_total_hours) || 0),
					basic_attendance_hours: basic_attendance_hours,
					employee_firstname: item.employee_firstname,
					week_range: date_range,
					start_of_working_hours: item.start_of_working_hours,
					end_of_working_hours: item.end_of_working_hours,
					day_abbr: item.day_abbr,
					employee_code: item.employee_code,
					leave_type: item.leave_type || '',
					holiday_description: item.holiday_description || '',
					comments: item.comments || '',
					leave_hours: leave_hours,
					is_resigned: item.is_resigned,
					holiday_hours: holiday_hours,
					overtime_type: item.overtime_type,
					overtime_hours: item.overtime_hours,
					total_attendance_hours: totals.total_attendance_hours,
					total_basic_attendance_hours: totals.basic_attendance_hours,
					total_overtime_hours: overtime_totals,
					total_leave_hours: totals.total_leaves_hours,
					total_holiday_hours: totals.total_holiday_hours
				});
			}
		});

		return {
			attendance_data: attendance_data,
			totals: totals,
			overtime_totals: overtime_totals,
			week_range: date_range,
		};
	}

	process_monthly_data(employee_data)
	{
		let attendance_data = [];
		let totals = {
			basic_attendance_hours: 0,
			total_attendance_hours: 0,
			total_leaves_hours: 0,
			total_holiday_hours: 0,
		};

		let overtime_totals = {};
		this.viewModel.overtime_types_columns().forEach(type => {
			overtime_totals[type.overtime_type] = 0;
		});

		employee_data.forEach((item) => {
			let work_day = new Date(item.work_day);
			let formatted_work_day = work_day.toLocaleDateString();

			function format_time(val)
			{
				if (val)
				{
					if (/^\d{2}:\d{2}(:\d{2})?$/.test(val))
						return val.slice(0, 5);
					return new Date(val).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
				}
				return '00:00';
			}

			let formatted_start_time = '00:00';
			let formatted_end_time = '00:00';

			if (work_day.getDay() == 0 || work_day.getDay() == 6)
			{
				formatted_start_time = format_time(item.task_start_time);
				formatted_end_time = format_time(item.task_end_time);
			}
			else
			{
				formatted_start_time = format_time(item.task_start_time || item.start_of_working_hours);
				formatted_end_time = format_time(item.task_end_time || item.end_of_working_hours);
			}

			let basic_attendance_hours = (work_day.getDay() === 6 || work_day.getDay() === 0)
				? 0
				: (item.basic_attendance_hours?.hours ?? 0);

			let task_total_hours = parseFloat(item.task_total_hours) || 0;

			let leave_hours = item.leave_hours ? parseFloat(item.leave_hours) : item.leave_type ?
				(item.task_start_time && item.task_end_time ? parseFloat(basic_attendance_hours) - task_total_hours : parseFloat(basic_attendance_hours)) : 0;

			let holiday_hours;
			if (item.holiday_hours)
				holiday_hours = parseFloat(item.holiday_hours) || 0;
			else if (item.holiday_description)
				holiday_hours = parseFloat(basic_attendance_hours) || 0;
			else
				holiday_hours = 0;

			totals.basic_attendance_hours += this.viewModel.convert_to_numeric(basic_attendance_hours) || 0;
			totals.total_attendance_hours += this.viewModel.convert_to_numeric(task_total_hours) || 0;
			totals.total_leaves_hours += this.viewModel.convert_to_numeric(leave_hours);
			totals.total_holiday_hours += this.viewModel.convert_to_numeric(holiday_hours);

			if (item.overtime_type && overtime_totals.hasOwnProperty(item.overtime_type))
			{
				let current_overtime = this.viewModel.convert_to_numeric(overtime_totals[item.overtime_type] || 0);
				let added_overtime = this.viewModel.convert_to_numeric(item.overtime_hours) || 0;
				overtime_totals[item.overtime_type] = current_overtime + added_overtime;
			}

			attendance_data.push({
				employee_id: item.employee_id,
				work_day: formatted_work_day,
				task_start_time: formatted_start_time,
				task_end_time: formatted_end_time,
				task_total_hours: ko.observable(parseFloat(task_total_hours) || 0),
				basic_attendance_hours: basic_attendance_hours,
				employee_firstname: item.employee_firstname,
				start_of_working_hours: item.start_of_working_hours,
				end_of_working_hours: item.end_of_working_hours,
				day_abbr: item.day_abbr,
				employee_code: item.employee_code,
				leave_type: item.leave_type || '',
				holiday_description: item.holiday_description || '',
				comments: item.comments || '',
				leave_hours: leave_hours,
				holiday_hours: holiday_hours,
				overtime_type: item.overtime_type,
				overtime_hours: item.overtime_hours,
				is_resigned: item.is_resigned
			});
		});

		return {
			attendance_data: attendance_data,
			totals: totals,
			overtime_totals: overtime_totals,
		};
	}

	format_interval(interval)
	{
		if (!interval) return 0;

		if (typeof interval === 'number')
			return interval;

		if (typeof interval === 'object')
		{
			let hours = interval.hours || 0;
			let minutes = interval.minutes || 0;
			return hours + (minutes / 60);
		}

		if (typeof interval === 'string')
		{
			if (interval.includes(':'))
			{
				let parts = interval.split(':');
				let hours = parseInt(parts[0]) || 0;
				let minutes = parseInt(parts[1]) || 0;
				return hours + (minutes / 60);
			}

			let matches = interval.match(/(\d+)/);
			if (matches)
			{
				let value = parseInt(matches[1]);
				if (value < 60)
					return value / 60;
				else
					return Math.floor(value / 60) + ((value % 60) / 60);
			}
		}

		return 0;
	}

	format_hours_display(hours)
	{
		if (typeof hours !== 'number' || isNaN(hours))
			return '0.00';
		return hours.toFixed(2);
	}

	get_week_start(date, start_day)
	{
		let days_of_week = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
		let start_index = days_of_week.indexOf(start_day);
		let current_day = date.getDay();
		let offset = current_day >= start_index ? current_day - start_index : 6 - start_index + current_day + 1;
		return new Date(date.getTime() - offset * 24 * 60 * 60 * 1000);
	}
}

export default {
	route: '[/]time_management/employee_hours',
	page_class: ReportEmployeeHoursPage,
	template: template,
	template_filename: 'translation/report_employee_hours',
}